import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnChanges,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { TuiDropdown, TuiDropdownManual } from '@taiga-ui/core'
import { TranslatePipe } from '@ngx-translate/core'
import { DropdownVariantEnum } from '@shared/enums/dropdown-variant.enum'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { DateTime } from 'luxon'
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { PositionOptionCloseComponent } from '@shared/components/trading/positions/options/common/position-option-close/position-option-close.component'
import { PositionOptionExecutionComponent } from '@shared/components/trading/positions/options/common/position-option-execution/position-option-execution.component'
import { PositionOptionInfoComponent } from '@shared/components/trading/positions/options/common/position-option-info/position-option-info.component'
import { SecondaryLiveTickerService } from '@modules/stocks-module/live-ticker/services/secondary-live-ticker.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@Component({
	selector: 'app-positions-options-actions',
	templateUrl: './positions-options-actions.component.html',
	styleUrl: './positions-options-actions.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		TuiDropdown,
		TuiDropdownManual,
		PositionOptionInfoComponent,
		PositionOptionExecutionComponent,
		PositionOptionCloseComponent,
		ClickOutsideDirective,
		ButtonComponent,
	],
})
export class PositionsOptionsActionsComponent implements OnChanges {
	protected readonly number: NumberConstructor = Number
	protected readonly date: DateConstructor = Date
	protected readonly dateTime: typeof DateTime = DateTime
	protected readonly dropdownVariantEnum: typeof DropdownVariantEnum = DropdownVariantEnum
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum

	public option: InputSignal<PositionOptionInterface | null> = input<PositionOptionInterface | null>(null)

	private readonly _secondaryLiveTickerService: SecondaryLiveTickerService =
		inject<SecondaryLiveTickerService>(SecondaryLiveTickerService)

	protected openedDropdown: WritableSignal<DropdownVariantEnum | null> = signal(null)

	protected isReached1515ET: WritableSignal<boolean> = signal(false)
	protected isReached1530ET: WritableSignal<boolean> = signal(false)
	protected canExecuteOption: Signal<boolean> = computed<boolean>(
		(): boolean => this.option()?.side === this.positionSideEnum.LONG,
	)
	private _checkReachedET(timeEType: '1515' | '1530'): void {
		const option: PositionOptionInterface | null = this.option()
		if (!option) return

		const [year, month, day]: (number | undefined)[] = option.option_date
			.split('-')
			.map((value: string): number => +value)

		if (!year || !month || !day) return

		if ([day, month, year].some((value: number | undefined): boolean => this.number.isNaN(value))) {
			return
		}

		const calendarDate: Date = new this.date(year, month - 1, day)
		const today: Date = new this.date()
		const todayStart: Date = new this.date(today.getFullYear(), today.getMonth(), today.getDate())

		const minute: number = timeEType === '1515' ? 15 : 30

		const thresholdET: DateTime = this.dateTime.fromObject(
			{ year, month, day, hour: 15, minute },
			{ zone: 'America/New_York' },
		)

		const nowET: DateTime = this.dateTime.now().setZone('America/New_York')
		const isReachedETTime: boolean = todayStart >= calendarDate && nowET.toMillis() >= thresholdET.toMillis()

		if (timeEType === '1515') {
			this.isReached1515ET.set(isReachedETTime)
		}
		if (timeEType === '1530') {
			this.isReached1530ET.set(isReachedETTime)
		}

		if (isReachedETTime) this.toggleDropdown(null)
	}

	protected toggleDropdown(dropdown: DropdownVariantEnum | null): void {
		if (this.openedDropdown() === dropdown) {
			this._secondaryLiveTickerService.closeLiveTicker()
			this.openedDropdown.set(null)
		} else {
			this.openedDropdown.set(dropdown)
		}
	}

	protected onClickOutside(): void {
		this._secondaryLiveTickerService.closeLiveTicker()
		this.openedDropdown.set(null)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['option']) {
			this._checkReachedET('1515')
			this._checkReachedET('1530')
		}
	}

	ngOnDestroy(): void {
		this._secondaryLiveTickerService.closeLiveTicker()
	}
}
